import React, { useEffect, createElement } from 'react';
// =====================================================================
// SEO CONFIG — pas de SITE_URL aan zodra de definitieve domeinnaam vaststaat.
// Voorbeelden: 'https://coreprint.nl' of 'https://coreprint.noorddigital.nl'
// =====================================================================
const SITE_URL = 'https://coreprint.noorddigital.nl';
const SITE_NAME = 'CorePrint';
const COMPANY = 'Noord Digital';
const LOCALE = 'nl_NL';
const LANG = 'nl';
const TITLE = 'CorePrint — Data-gedreven B2B SEO & Search Everywhere Optimization | Noord Digital';
const DESCRIPTION = 'CorePrint is de sonar-aanpak voor B2B-bedrijven: 90 dagen data verzamelen via Google Ads, daarna pas SEO bouwen op bewezen zoekwoorden. Inclusief Search Everywhere Optimization voor ChatGPT, Perplexity en Google AI Overviews. Marktexclusiviteit per regio. Een dienst van Noord Digital uit Alkmaar.';
const KEYWORDS = ['CorePrint', 'Noord Digital', 'B2B SEO', 'data-gedreven SEO', 'datagedreven SEO', 'SEO bureau B2B', 'SEO bureau Alkmaar', 'SEO bureau Noord-Holland', 'Search Everywhere Optimization', 'SEO voor ChatGPT', 'SEO voor Perplexity', 'AI SEO', 'Google AI Overviews SEO', 'AI zoekmachine optimalisatie', 'klantacquisitie B2B', 'leadgeneratie B2B', 'Google Ads sonar', 'marktanalyse SEO', 'zoekwoordonderzoek B2B', 'voorspelbare klantacquisitie', 'SEO strategie', 'DataSprint'].join(', ');
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;
// FAQ data — moet matchen met FAQ.tsx voor consistente structured data
const FAQ_ITEMS: {
  q: string;
  a: string;
}[] = [{
  q: 'Waarom de vissersmetafoor?',
  a: "Omdat het precies beschrijft wat we anders doen. De meeste bureaus bouwen een groter net (meer pagina's). Wij peilen eerst waar de vis zit (welke zoekwoorden converteren). Dat is het verschil tussen hopen en weten."
}, {
  q: 'Wat is Search Everywhere Optimization?',
  a: 'SEO is niet meer alleen Google. Je klanten zoeken ook via ChatGPT, Perplexity, Google AI Overviews en andere AI-tools. CorePrint zorgt ervoor dat je overal zichtbaar bent waar je klanten zoeken, niet alleen in de traditionele zoekresultaten.'
}, {
  q: 'Wat kost CorePrint?',
  a: 'CorePrint bestaat uit twee fases: de DataSprint (90 dagen adverteren plus analyse) en de bouwfase (SEO op basis van de data). We bespreken de exacte investering in de presentatie, afgestemd op jouw markt en schaal.'
}, {
  q: 'Wat als de data tegenvalt?',
  a: 'De data valt nooit tegen, want hij vertelt altijd de waarheid. Ook als de uitkomst is dat een kanaal niet werkt voor jouw markt, is dat waardevolle informatie. Zo bespaar je jaren van inefficiënte investering.'
}, {
  q: 'Hoe weet ik of mijn markt nog vrij is?',
  a: 'Dat check je in het gratis kennismakingsgesprek. Zodra één concurrent in jouw markt start, sluit die markt. We werken altijd met één klant per markt.'
}, {
  q: 'Moet ik zelf iets doen?',
  a: 'Minimaal. Je geeft ons toegang tot je doelgroep, je doelen en je markt. Wij regelen de rest: campagne, data-analyse, rapport en bouw. Jij focust op je klanten.'
}, {
  q: 'Hoe lang duurt het voor ik resultaat zie?',
  a: 'In de eerste 4 tot 6 weken zie je al welke boodschappen aanslaan (kwalitatieve data). Na 90 dagen heb je het volledige CorePrint-rapport. Daarna bouwt organisch verkeer maand over maand.'
}];
function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function setLink(rel: string, href: string, extra?: Record<string, string>) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  if (extra) Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
}
function setJsonLd(id: string, data: object) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
export function SEO() {
  useEffect(() => {
    // <html lang="nl">
    document.documentElement.setAttribute('lang', LANG);
    // Title
    document.title = TITLE;
    // Standaard meta tags
    setMeta('name', 'description', DESCRIPTION);
    setMeta('name', 'keywords', KEYWORDS);
    setMeta('name', 'author', COMPANY);
    setMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMeta('name', 'googlebot', 'index, follow');
    setMeta('name', 'theme-color', '#050A14');
    setMeta('name', 'application-name', SITE_NAME);
    setMeta('name', 'apple-mobile-web-app-title', SITE_NAME);
    setMeta('name', 'format-detection', 'telephone=no');
    // Geo tagging (lokale SEO)
    setMeta('name', 'geo.region', 'NL-NH');
    setMeta('name', 'geo.placename', 'Alkmaar');
    setMeta('name', 'geo.position', '52.6324;4.7534');
    setMeta('name', 'ICBM', '52.6324, 4.7534');
    // Open Graph
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:title', TITLE);
    setMeta('property', 'og:description', DESCRIPTION);
    setMeta('property', 'og:url', SITE_URL);
    setMeta('property', 'og:locale', LOCALE);
    setMeta('property', 'og:image', OG_IMAGE);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:image:alt', `${SITE_NAME} — Data-gedreven B2B SEO`);
    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', TITLE);
    setMeta('name', 'twitter:description', DESCRIPTION);
    setMeta('name', 'twitter:image', OG_IMAGE);
    setMeta('name', 'twitter:image:alt', `${SITE_NAME} — Data-gedreven B2B SEO`);
    // Canonical + alt language
    setLink('canonical', SITE_URL);
    setLink('alternate', SITE_URL, {
      hreflang: 'nl-NL'
    });
    // Performance: preconnect Google Fonts (font wordt al geladen via index.css)
    setLink('preconnect', 'https://api.fontshare.com', {
      crossorigin: ''
    });
    // JSON-LD #1 — Organization
    setJsonLd('ld-organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: COMPANY,
      alternateName: SITE_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
      description: DESCRIPTION,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Alkmaar',
        addressRegion: 'Noord-Holland',
        addressCountry: 'NL'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: 'info@noorddigital.nl',
        availableLanguage: ['Dutch', 'English'],
        areaServed: 'NL'
      },
      sameAs: ['https://www.linkedin.com/company/noord-digital', 'https://www.instagram.com/noord_digital/', 'https://noorddigital.nl']
    });
    // JSON-LD #2 — WebSite + sitelinks searchbox potential
    setJsonLd('ld-website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
      inLanguage: 'nl-NL',
      publisher: {
        '@type': 'Organization',
        name: COMPANY
      }
    });
    // JSON-LD #3 — Service
    setJsonLd('ld-service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Data-gedreven B2B SEO',
      name: 'CorePrint — sonar-aanpak voor B2B SEO',
      description: DESCRIPTION,
      provider: {
        '@type': 'Organization',
        name: COMPANY,
        url: 'https://noorddigital.nl'
      },
      areaServed: {
        '@type': 'Country',
        name: 'Nederland'
      },
      audience: {
        '@type': 'BusinessAudience',
        audienceType: 'B2B-bedrijven'
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'CorePrint diensten',
        itemListElement: [{
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '90 dagen DataSprint',
            description: 'Gerichte Google Ads-campagne als sonar om te meten welke zoekwoorden converteren in jouw B2B-markt.'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'CorePrint-rapport',
            description: 'Complete marktanalyse met bewezen zoekwoorden, conversiepaden en prioriteitenkaart.'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Search Everywhere Optimization',
            description: 'Zichtbaarheid in Google, ChatGPT, Perplexity, Google AI Overviews en andere AI-zoekmachines.'
          }
        }]
      }
    });
    // JSON-LD #4 — FAQPage
    setJsonLd('ld-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a
        }
      }))
    });
    // JSON-LD #5 — BreadcrumbList
    setJsonLd('ld-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Noord Digital',
        item: 'https://noorddigital.nl'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'CorePrint',
        item: SITE_URL
      }]
    });
  }, []);
  return null;
}